import React from 'react';
import type { Manifests } from '../../api-client';
import {
  getFilterLabels,
  type FilterKey,
  type FilterValue,
  getFilterValues,
  getImpactValue
} from '../../hooks/useIssuesFilter';
import styles from './IssueFilters.css';
import { Panel, Tag, TagLabel } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import { getGuides } from '../../utils/get-guides';
import type { Impact, Filters } from '@deque/axe-devtools-issues-utils';

export interface IssuesFilterProps {
  filters: Filters;
  supportedFiltersTags?: FilterKey[];
  manifests: Manifests | null;
  additionalTags?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

const DEFAULT_SUPPORTED_FILTERS_TAGS: FilterKey[] = [
  'automatic',
  'bestPractices',
  'configAccessibilityStandard',
  'configBestPractices',
  'configNeedsReview',
  'configWCAGLevel',
  'igt',
  'impact',
  'manifestId',
  'manual',
  'needsReview',
  'igt'
];

const IssueFilters = ({
  filters,
  manifests,
  additionalTags,
  children,
  footer,
  supportedFiltersTags = DEFAULT_SUPPORTED_FILTERS_TAGS
}: IssuesFilterProps) => {
  const { t } = useTranslation();

  const filterLabels = getFilterLabels(t);

  const manifest = Object.values(manifests || {})
    .flat()
    .find(m => m.id === filters.manifestId);

  const guide =
    manifest && getGuides(t).find(g => g.name === manifest.guide)?.heading;

  const tags = Object.keys(filters).reduce((acc, filter) => {
    const f = filter as FilterKey;

    if (supportedFiltersTags && !supportedFiltersTags.includes(f)) {
      return acc;
    }

    if (
      filter === 'configBestPractices' ||
      filter === 'configNeedsReview' ||
      (filter === 'igt' && (!!manifest || !filters.igt)) ||
      (filter === 'manifestId' && !manifest) ||
      (filter === 'needsReview' && !filters.needsReview) ||
      (filter === 'impact' && !filters.impact.length) ||
      (filter === 'bestPractices' && !filters.bestPractices) ||
      (filter === 'automatic' && !filters.automatic) ||
      (filter === 'manual' && !filters.manual) ||
      (filter === 'configWCAGLevel' && filters.configAccessibilityStandard) ||
      (filter === 'newIssues' && !filters.newIssues) ||
      (filter === 'pageStateKey' && !filters.pageStateKey)
    ) {
      return acc;
    }

    let filterValue;

    if (filter === 'manifestId' && !!guide && !!manifest) {
      filterValue = `${guide} (${manifest.name})`;
    } else if (filter === 'impact' && filters[f] !== 'none') {
      filterValue = getImpactValue(filters[f] as Impact, t);
    } else {
      filterValue = getFilterValues(f, t)?.get(filters[f] as FilterValue);
    }

    return [
      ...acc,
      <Tag key={filter}>
        <TagLabel>{`${filterLabels[f].label}:`}&nbsp;</TagLabel>
        <span>{filterValue}</span>
      </Tag>
    ];
  }, [] as React.ReactNode[]);

  return (
    <Panel aria-labelledby="filters-label" className={styles.filterBar}>
      <div className={styles.filterHeader}>
        <div className={styles.filtersContainer}>
          <h2 id="filters-label" className={styles.filterBarLabel}>
            {t('Applied Filters:')}
          </h2>
          {Object.keys(filterLabels).length && (
            <div className={styles.filterTagsContainer}>
              {tags}
              {additionalTags}
            </div>
          )}
        </div>
        {!!children && (
          <div className={styles.filterActionsContainer}>{children}</div>
        )}
      </div>
      {!!footer && <div className={styles.filterFooter}>{footer}</div>}
    </Panel>
  );
};

export default IssueFilters;
